import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const OwnerContact = ({ owner, property }) => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    visitDate: '',
    visitTime: ''
  });
  const [activeTab, setActiveTab] = useState('contact');

  const tabs = [
    { id: 'contact', label: 'Contact Owner', icon: 'Phone' },
    { id: 'schedule', label: 'Schedule Visit', icon: 'Calendar' },
    { id: 'inquiry', label: 'Send Inquiry', icon: 'MessageCircle' }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert('Your inquiry has been sent successfully!');
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: '',
      visitDate: '',
      visitTime: ''
    });
  };

  const handleWhatsAppClick = () => {
    const message = `Hi, I'm interested in your property: ${property.title} located at ${property.location}. Can we discuss the details?`;
    const whatsappUrl = `https://wa.me/${owner.phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCallClick = () => {
    window.location.href = `tel:${owner.phone}`;
  };

  return (
    <div className="bg-surface rounded-lg shadow-property-card">
      {/* Owner Profile */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            {owner.avatar ? (
              <img
                src={owner.avatar}
                alt={owner.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <Icon name="User" size={24} className="text-primary" />
            )}
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-lg font-semibold text-text-primary">{owner.name}</h3>
              {owner.verified && (
                <div className="flex items-center gap-1 bg-success/10 text-success px-2 py-1 rounded-full text-xs">
                  <Icon name="Shield" size={12} />
                  <span>Verified</span>
                </div>
              )}
            </div>
            <div className="text-sm text-text-secondary">{owner.type}</div>
            <div className="flex items-center gap-4 mt-2 text-sm">
              <div className="flex items-center gap-1">
                <Icon name="Star" size={14} className="text-warning" />
                <span>{owner.rating}</span>
              </div>
              <div className="text-text-secondary">
                {owner.propertiesListed} properties listed
              </div>
              <div className="text-text-secondary">
                Joined {owner.joinedDate}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Contact Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="default"
            iconName="Phone"
            iconPosition="left"
            onClick={handleCallClick}
            className="flex-1"
          >
            Call Now
          </Button>
          <Button
            variant="secondary"
            iconName="MessageCircle"
            iconPosition="left"
            onClick={handleWhatsAppClick}
            className="flex-1"
          >
            WhatsApp
          </Button>
          <Button
            variant="outline"
            iconName="Mail"
            iconPosition="left"
            onClick={() => setShowContactForm(true)}
            className="flex-1"
          >
            Email
          </Button>
        </div>
      </div>

      {/* Contact Form Tabs */}
      <div className="border-b border-border">
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-smooth ${
                activeTab === tab.id
                  ? 'text-primary border-b-2 border-primary' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'contact' && (
          <div className="space-y-4">
            <div className="bg-muted p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Phone" size={16} className="text-primary" />
                <span className="font-medium text-text-primary">Phone Number</span>
              </div>
              <div className="text-text-primary font-mono">{owner.phone}</div>
              <div className="text-sm text-text-secondary mt-1">
                Available: {owner.availability}
              </div>
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Mail" size={16} className="text-primary" />
                <span className="font-medium text-text-primary">Email Address</span>
              </div>
              <div className="text-text-primary">{owner.email}</div>
              <div className="text-sm text-text-secondary mt-1">
                Response time: Usually within 2 hours
              </div>
            </div>

            <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Info" size={16} className="text-primary" />
                <span className="font-medium text-text-primary">Contact Tips</span>
              </div>
              <ul className="text-sm text-text-secondary space-y-1">
                <li>• Best time to call: {owner.bestTimeToCall}</li>
                <li>• Preferred communication: {owner.preferredContact}</li>
                <li>• Languages spoken: {owner.languages.join(', ')}</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'schedule' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Your Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter your full name"
              />
              <Input
                label="Phone Number"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                required
                placeholder="Enter your phone number"
              />
            </div>

            <Input
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="Enter your email address"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Preferred Visit Date"
                name="visitDate"
                type="date"
                value={formData.visitDate}
                onChange={handleInputChange}
                required
              />
              <Input
                label="Preferred Time"
                name="visitTime"
                type="time"
                value={formData.visitTime}
                onChange={handleInputChange}
                required
              />
            </div>

            <Input
              label="Additional Message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              placeholder="Any specific requirements or questions..."
            />

            <Button
              type="submit"
              variant="default"
              iconName="Calendar"
              iconPosition="left"
              fullWidth
            >
              Schedule Visit
            </Button>
          </form>
        )}

        {activeTab === 'inquiry' && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Your Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="Enter your full name"
              />
              <Input
                label="Phone Number"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                required
                placeholder="Enter your phone number"
              />
            </div>

            <Input
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="Enter your email address"
            />

            <div className="space-y-2">
              <label className="block text-sm font-medium text-text-primary">
                Your Message
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                placeholder="I'm interested in this property. Please provide more details about..."
              />
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Clock" size={16} className="text-primary" />
                <span className="font-medium text-text-primary">Response Time</span>
              </div>
              <div className="text-sm text-text-secondary">
                The owner typically responds within 2-4 hours during business hours.
              </div>
            </div>

            <Button
              type="submit"
              variant="default"
              iconName="Send"
              iconPosition="left"
              fullWidth
            >
              Send Inquiry
            </Button>
          </form>
        )}
      </div>

      {/* Recent Activity */}
      <div className="p-6 border-t border-border">
        <h4 className="font-medium text-text-primary mb-3">Recent Activity</h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-text-secondary">
            <Icon name="Eye" size={14} />
            <span>12 people viewed this property today</span>
          </div>
          <div className="flex items-center gap-2 text-text-secondary">
            <Icon name="MessageCircle" size={14} />
            <span>3 inquiries received this week</span>
          </div>
          <div className="flex items-center gap-2 text-text-secondary">
            <Icon name="Calendar" size={14} />
            <span>Last updated 2 days ago</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerContact;